import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getAllUsers, createUser, updateUser, deleteUser, updateUserLock, ROLES } from "../services/firebaseAuth";
import { generatePasswordMessage, copyToClipboard } from "../utils/passwordSharingTemplate";
import { logUserCreation, logUserUpdate, logUserDeletion, logPasswordChange } from "../utils/loggingWrapper";
import { FaPlus, FaEdit, FaTrash, FaUser, FaLock, FaCopy, FaWhatsapp } from "react-icons/fa";

const SubAdminManagementWithLogging = () => {
    const [subAdmins, setSubAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [formData, setFormData] = useState({
        username: "",
        password: "",
        role: "sub_manager",
        name: "",
        email: ""
    });
    const [generatedPassword, setGeneratedPassword] = useState("");
    const [showSharingOptions, setShowSharingOptions] = useState(false);
    const [newUserCredentials, setNewUserCredentials] = useState(null);
    const { user, hasPermission, PERMISSIONS } = useAuth();

    // Fetch users from Firebase
    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            const result = await getAllUsers();
            if (result.success) {
                // Filter out super admins and show only managers and sub managers
                const filteredUsers = result.users.filter(user =>
                    user.role === ROLES.MANAGER || user.role === ROLES.SUB_MANAGER
                );
                setSubAdmins(filteredUsers);
            } else {
                console.error('Error fetching users:', result.error);
                setSubAdmins([]);
            }
            setLoading(false);
        };

        fetchUsers();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingAdmin) {
                // Update existing user - pass old role to handle role changes
                const result = await updateUser(editingAdmin.id, formData, formData.role, editingAdmin.role);
                if (result.success) {
                    // Log the activity
                    await logUserUpdate(
                        user,
                        editingAdmin.id,
                        formData.name || editingAdmin.name,
                        formData.role,
                        {
                            changes: formData,
                            previousData: editingAdmin
                        }
                    );

                    // Log password change if password was updated
                    if (formData.password) {
                        await logPasswordChange(user, editingAdmin.id, formData.name || editingAdmin.name);
                    }

                    // Refresh the list
                    const fetchResult = await getAllUsers();
                    if (fetchResult.success) {
                        const filteredUsers = fetchResult.users.filter(user =>
                            user.role === ROLES.MANAGER || user.role === ROLES.SUB_MANAGER
                        );
                        setSubAdmins(filteredUsers);
                    }
                } else {
                    alert('Failed to update user: ' + result.error);
                    return;
                }
            } else {
                // Create new user
                const result = await createUser(formData, formData.role);
                if (result.success) {
                    // Log the activity
                    await logUserCreation(
                        user,
                        result.id,
                        formData.name || formData.username,
                        formData.role
                    );

                    // Store credentials for sharing
                    setNewUserCredentials({
                        username: formData.username,
                        password: formData.password,
                        role: formData.role,
                        name: formData.name
                    });
                    setShowSharingOptions(true);

                    // Refresh the list
                    const fetchResult = await getAllUsers();
                    if (fetchResult.success) {
                        const filteredUsers = fetchResult.users.filter(user =>
                            user.role === ROLES.MANAGER || user.role === ROLES.SUB_MANAGER
                        );
                        setSubAdmins(filteredUsers);
                    }
                } else {
                    alert('Failed to create user: ' + result.error);
                    return;
                }
            }

            setFormData({
                username: "",
                password: "",
                role: "sub_manager",
                name: "",
                email: ""
            });
            setShowModal(false);
            setEditingAdmin(null);
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('An error occurred. Please try again.');
        }
    };

    const handleEdit = (admin) => {
        setEditingAdmin(admin);
        setFormData({
            username: admin.username,
            password: "",
            role: admin.role,
            name: admin.name,
            email: admin.email
        });
        setShowModal(true);
    };

    const handleLockToggle = async (admin) => {
        const newLocked = !(admin.locked === true || admin.locked === "true");
        const action = newLocked ? "lock" : "unlock";
        if (!window.confirm(`Are you sure you want to ${action} this account?`)) return;
        try {
            const result = await updateUserLock(admin.id, admin.role, newLocked);
            if (result.success) {
                setSubAdmins(prev =>
                    prev.map(a =>
                        a.id === admin.id ? { ...a, locked: newLocked } : a
                    )
                );
                alert(`Account ${action}ed successfully.`);
            } else {
                alert("Failed to update lock status: " + (result.error || "Unknown error"));
            }
        } catch (err) {
            console.error(err);
            alert("Failed to update lock status.");
        }
    };

    const handleDelete = async (adminId, adminRole) => {
        if (window.confirm("Are you sure you want to delete this sub admin?")) {
            try {
                // Find the admin to get their name for logging
                const adminToDelete = subAdmins.find(admin => admin.id === adminId);
                const adminName = adminToDelete?.name || adminToDelete?.username || 'Unknown';

                const result = await deleteUser(adminId, adminRole);
                if (result.success) {
                    // Log the activity
                    await logUserDeletion(user, adminId, adminName, adminRole);

                    // Refresh the list
                    const fetchResult = await getAllUsers();
                    if (fetchResult.success) {
                        const filteredUsers = fetchResult.users.filter(user =>
                            user.role === ROLES.MANAGER || user.role === ROLES.SUB_MANAGER
                        );
                        setSubAdmins(filteredUsers);
                    }
                } else {
                    alert('Failed to delete user: ' + result.error);
                }
            } catch (error) {
                console.error('Error deleting user:', error);
                alert('An error occurred while deleting the user.');
            }
        }
    };

    const generatePassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setGeneratedPassword(password);
        setFormData(prev => ({ ...prev, password: password }));
    };

    const handleAddNew = () => {
        setEditingAdmin(null);
        setFormData({
            username: "",
            password: "",
            role: "sub_manager",
            name: "",
            email: ""
        });
        setGeneratedPassword("");
        setShowModal(true);
    };

    const handleCopyCredentials = async () => {
        if (newUserCredentials) {
            const message = generatePasswordMessage(
                newUserCredentials.username,
                newUserCredentials.password,
                newUserCredentials.role,
                newUserCredentials.name
            );

            const success = await copyToClipboard(message);
            if (success) {
                alert('Credentials copied to clipboard! You can now paste it in WhatsApp or email.');
            } else {
                alert('Failed to copy to clipboard. Please copy manually.');
            }
        }
    };

    const handleWhatsAppShare = () => {
        if (newUserCredentials) {
            const message = generatePasswordMessage(
                newUserCredentials.username,
                newUserCredentials.password,
                newUserCredentials.role,
                newUserCredentials.name
            );

            const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
        }
    };

    const closeSharingOptions = () => {
        setShowSharingOptions(false);
        setNewUserCredentials(null);
    };

    if (loading) {
        return (
            <div className="loading">
                <span className="loader"></span>
                <style>{`
          .loading {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            color: #666;
          }
          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #000;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          [data-theme="dark"] .loader::after {
            border: 2px solid #FFF;
          }
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
            </div>
        );
    }

    return (
        <div className="subadmin-panel">
            <style>{`
        .subadmin-panel {
          padding: 30px;
          margin: 0 auto;
          background: #f9fafb;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e5e7eb;
        }
        h2 {
          color: #1f2937;
          font-size: 20px;
          margin: 0;
        }
        .add-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
          transition: background 0.2s;
        }
        .add-btn:hover {
          background: #2563eb;
        }
        .table-container {
          overflow-x: auto;
          min-height: 300px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          background: #f3f4f6;
          color: #4a5568;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
          border-bottom: 2px solid #e5e7eb;
          position: sticky;
          top: 0;
        }
        td {
          padding: 12px;
          border-bottom: 1px solid #e5e7eb;
          font-size: 14px;
          color: #4a5568;
        }
        .role-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
        }
        .role-badge.manager {
          background: #dbeafe;
          color: #1e40af;
        }
        .role-badge.sub_manager {
          background: #fef3c7;
          color: #92400e;
        }
        .actions {
          display: flex;
          gap: 8px;
        }
        .action-btn {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          border: 1px solid #d1d5db;
          background: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .action-btn.edit-btn:hover {
          background: #3b82f6;
          color: white;
        }
        .action-btn.delete-btn:hover {
          background: #ef4444;
          color: white;
        }
        .action-btn.lock-btn {
          color: #6b7280;
        }
        .action-btn.lock-btn:hover {
          background: #f59e0b;
          color: white;
          border-color: #f59e0b;
        }
        .action-btn.lock-btn.locked {
          background: #f59e0b;
          color: white;
          border-color: #f59e0b;
        }
        .action-btn.lock-btn.locked:hover {
          background: #d97706;
          border-color: #d97706;
        }
        .modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          padding: 30px;
          border-radius: 8px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .modal-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
        }
        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #6b7280;
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
          color: #374151;
        }
        .form-input {
          width: 100%;
          padding: 10px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 14px;
        }
        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .form-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }
        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }
        .btn-primary {
          background: #3b82f6;
          color: white;
        }
        .btn-primary:hover {
          background: #2563eb;
        }
        .btn-secondary {
          background: #6b7280;
          color: white;
        }
        .btn-secondary:hover {
          background: #4b5563;
        }
        .no-results {
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }
      `}</style>

            <div className="header">
                <h2>Sub Admin Management</h2>
                {hasPermission(PERMISSIONS.USERS.CREATE) && (
                    <button className="add-btn" onClick={handleAddNew}>
                        <FaPlus /> Add Sub Admin
                    </button>
                )}
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Created Date</th>
                            <th>Last Login</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subAdmins.length > 0 ? (
                            subAdmins.map((admin) => (
                                <tr key={admin.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FaUser style={{ color: '#6b7280' }} />
                                            {admin.username}
                                        </div>
                                    </td>
                                    <td>{admin.name}</td>
                                    <td>{admin.email}</td>
                                    <td>
                                        <span className={`role-badge ${admin.role}`}>
                                            {admin.role.replace('_', ' ')}
                                        </span>
                                    </td>
                                    <td>{admin.createdDate || 'N/A'}</td>
                                    <td>{admin.lastLogin || 'Never'}</td>
                                    <td className="actions">
                                        {hasPermission(PERMISSIONS.USERS.MANAGE) && (
                                            <>
                                                <button
                                                    className="action-btn edit-btn"
                                                    onClick={() => handleEdit(admin)}
                                                    title="Edit"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    className={`action-btn lock-btn ${admin.locked ? "locked" : ""}`}
                                                    onClick={() => handleLockToggle(admin)}
                                                    title={admin.locked ? "Unlock Account" : "Lock Account"}
                                                >
                                                    <FaLock />
                                                </button>
                                                <button
                                                    className="action-btn delete-btn"
                                                    onClick={() => handleDelete(admin.id, admin.role)}
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="no-results">
                                    No sub admins found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingAdmin ? 'Edit Sub Admin' : 'Add New Sub Admin'}
                            </h3>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label">Username</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    Password {editingAdmin && '(leave blank to keep current)'}
                                </label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        required={!editingAdmin}
                                        style={{ flex: 1 }}
                                        placeholder={editingAdmin ? "Enter new password or leave blank" : "Enter password"}
                                    />
                                    {!editingAdmin && (
                                        <button
                                            type="button"
                                            onClick={generatePassword}
                                            style={{
                                                padding: '8px 12px',
                                                backgroundColor: '#28a745',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            Generate
                                        </button>
                                    )}
                                </div>
                                {generatedPassword && !editingAdmin && (
                                    <div style={{
                                        marginTop: '8px',
                                        padding: '8px',
                                        backgroundColor: '#e7f3ff',
                                        border: '1px solid #b3d9ff',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                    }}>
                                        <strong>Generated Password:</strong> {generatedPassword}
                                        <br />
                                        <em>Share this password with the user via WhatsApp or other means.</em>
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Role</label>
                                <select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    required
                                >
                                    <option value="sub_manager">Sub Manager</option>
                                    <option value="manager">Manager</option>
                                </select>
                            </div>
                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingAdmin ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Sharing Options Modal */}
            {showSharingOptions && newUserCredentials && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3 className="modal-title">Share User Credentials</h3>
                            <button className="close-btn" onClick={closeSharingOptions}>
                                ×
                            </button>
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <h4>User Created Successfully!</h4>
                            <p><strong>Username:</strong> {newUserCredentials.username}</p>
                            <p><strong>Password:</strong> {newUserCredentials.password}</p>
                            <p><strong>Role:</strong> {newUserCredentials.role.replace('_', ' ').toUpperCase()}</p>
                            <p><strong>Name:</strong> {newUserCredentials.name}</p>
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <h4>Share Options:</h4>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <button
                                    onClick={handleCopyCredentials}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <FaCopy /> Copy to Clipboard
                                </button>
                                <button
                                    onClick={handleWhatsAppShare}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#25D366',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <FaWhatsapp /> Share via WhatsApp
                                </button>
                            </div>
                        </div>
                        <div style={{
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}>
                            <strong>Note:</strong> Share these credentials securely with the user.
                            They can use these to log in to the admin panel.
                        </div>
                        <div style={{ marginTop: '20px', textAlign: 'right' }}>
                            <button
                                onClick={closeSharingOptions}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubAdminManagementWithLogging;
